import dayjs from 'dayjs';
import i18next from 'i18next';
import z, { type ZodSchema } from 'zod';
import { parseAndValidatePhone } from '../../../lib/phone-validation';
import { validatePostcode } from '../../../lib/postcode-validation';
import type { FieldType } from './InformationCard.type';
import type { CountryCode } from 'libphonenumber-js/max';

export type FieldValidationRules = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  countries?: CountryCode[];
};

function applyLengthBounds(
  schema: z.ZodString,
  { minLength, maxLength }: FieldValidationRules,
): z.ZodString {
  let next = schema;
  if (minLength !== undefined) {
    next = next.min(minLength, i18next.t('formErrors.tooShort', { count: minLength }));
  }
  if (maxLength !== undefined) {
    next = next.max(maxLength, i18next.t('formErrors.tooLong', { count: maxLength }));
  }
  return next;
}

export function buildFieldSchema(type: FieldType, rules: FieldValidationRules = {}): ZodSchema {
  const { required } = rules;
  const requiredMessage = i18next.t('formErrors.required');

  switch (type) {
    case 'number': {
      const schema = z.number({
        required_error: requiredMessage,
        invalid_type_error: i18next.t('formErrors.invalidNumber'),
      });
      return required ? schema : schema.optional();
    }
    case 'boolean':
      return required ? z.boolean({ required_error: requiredMessage }) : z.boolean().optional();
    case 'date': {
      const schema = z
        .string({
          required_error: requiredMessage,
          invalid_type_error: i18next.t('formErrors.invalidDate'),
        })
        .refine(value => dayjs(value).isValid(), i18next.t('formErrors.invalidDate'));
      return required ? schema : schema.optional();
    }
    case 'postcode': {
      const { countries } = rules;
      const schema = z.string({ required_error: requiredMessage }).superRefine((value, ctx) => {
        if (!value) {
          if (required) {
            ctx.addIssue({
              code: 'custom',
              message: requiredMessage,
              fatal: true,
            });
          }
          return;
        }
        if (!validatePostcode(value, countries)) {
          ctx.addIssue({
            code: 'custom',
            message: i18next.t(
              countries?.length
                ? 'formErrors.postcodeInvalidCountry'
                : 'formErrors.postcodeInvalid',
            ),
            fatal: true,
          });
        }
      });
      return required ? schema : schema.optional();
    }
    case 'tel': {
      const { countries } = rules;

      const schema = z.string({ required_error: requiredMessage }).transform((value, ctx) => {
        if (!value) {
          if (required) {
            ctx.addIssue({
              code: 'custom',
              message: i18next.t('formErrors.phoneInvalid'),
              fatal: true,
            });
            return z.NEVER;
          }
          return undefined;
        }
        return parseAndValidatePhone(value, ctx, countries);
      });
      return schema;
    }
    case 'email': {
      const schema = z
        .string({ required_error: requiredMessage })
        .email(i18next.t('formErrors.invalidEmail'));
      return required
        ? applyLengthBounds(schema, rules)
        : applyLengthBounds(schema, rules).optional();
    }
    case 'siret': {
      const schema = z
        .string({ required_error: requiredMessage })
        .refine(
          value => /^[0-9]{14}$/.test(value),
          i18next.t('formErrors.tooShort', { count: 14 }),
        );
      return required ? schema : schema.optional();
    }
    case 'text':
    default: {
      let schema = z.string({ required_error: requiredMessage });
      schema = applyLengthBounds(schema, rules);
      if (required) {
        return schema.min(1, requiredMessage);
      }
      return schema.optional();
    }
  }
}
